import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";

export interface EnquirySummaryQuery {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: any[];
}

export interface EnquiryListQuery {
  page: number;
  pageSize: number;
  filterType: 'all' | 'with_site_visit' | 'without_site_visit';
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: any[];
  sorting?: any[];
  filters?: any[];
}

// Fetch enquiry summary statistics (for the summary cards)
export const fetchEnquirySummary = createAsyncThunk(
  "enquirySummary/fetchSummary",
  async (query: EnquirySummaryQuery, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      // Add date filters
      if (query.dateFrom) {
        params.append("date_from", query.dateFrom.toISOString().split('T')[0]);
      }
      if (query.dateTo) {
        params.append("date_to", query.dateTo.toISOString().split('T')[0]);
      }

      // Add warehouse filters (exclude "all" option)
      if (query.warehouse && query.warehouse.length > 0) {
        const warehouseIds = query.warehouse
          .map((w: any) => w.warehouse_id)
          .filter((id: any) => id !== "all" && id !== "All Warehouses");
        
        if (warehouseIds.length > 0) {
          if (warehouseIds.length === 1) {
            params.append("warehouse_id", warehouseIds[0].toString());
          } else {
            params.append("warehouse_id", warehouseIds.join(','));
          }
        }
      }

      const url = `${api?.dashboad?.enquiry_summary}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await Axios.get(url);
      
      if (response.data?.data) {
        return response.data.data;
      }

      // Return default values if data is missing
      return {
        total_enquiries: 0,
        with_site_visit: 0,
        without_site_visit: 0,
      };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || "Network Error");
    }
  }
);

// Fetch enquiry list (for the table)
export const fetchEnquiryList = createAsyncThunk(
  "enquirySummary/fetchEnquiryList",
  async (query: EnquiryListQuery, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      // Pagination
      params.append("page", query.page.toString());
      params.append("page_size", query.pageSize.toString());

      // Filter type
      params.append("filter_type", query.filterType);

      // Date filters
      if (query.dateFrom) {
        params.append("date_from", query.dateFrom.toISOString().split('T')[0]);
      }
      if (query.dateTo) {
        params.append("date_to", query.dateTo.toISOString().split('T')[0]);
      }

      // Warehouse filters
      if (query.warehouse && query.warehouse.length > 0) {
        const warehouseIds = query.warehouse
          .map((w: any) => w.warehouse_id)
          .filter((id: any) => id !== "all" && id !== "All Warehouses");
        
        if (warehouseIds.length > 0) {
          if (warehouseIds.length === 1) {
            params.append("warehouse_id", warehouseIds[0].toString());
          } else {
            params.append("warehouse_id", warehouseIds.join(','));
          }
        }
      }

      // Sorting
      if (query.sorting && query.sorting.length > 0) {
        const sortField = query.sorting[0].id;
        const sortDirection = query.sorting[0].desc ? '-' : '';
        params.append("ordering", `${sortDirection}${sortField}`);
      }

      // Column filters
      if (Array.isArray(query.filters)) {
        query.filters.forEach((filter: any) => {
          if (filter.filterField && filter.filterValue !== undefined && filter.filterValue !== null) {
            // Handle date range filters
            if (
              typeof filter.filterValue === "object" &&
              filter.filterValue !== null &&
              !Array.isArray(filter.filterValue)
            ) {
              const { from_date, to_date } = filter.filterValue;
              if (
                from_date &&
                (filter.filterField === "created_at" ||
                  filter.filterField === "updated_at" ||
                  filter.filterField === "enquiry_date")
              ) {
                params.append(`${filter.filterField}_from`, from_date);
              }
              if (
                to_date &&
                (filter.filterField === "created_at" ||
                  filter.filterField === "updated_at" ||
                  filter.filterField === "enquiry_date")
              ) {
                params.append(`${filter.filterField}_to`, to_date);
              }
            } else {
              // Regular filter value (string, number, etc.)
              params.append(filter.filterField, String(filter.filterValue));
            }
          }
        });
      }

      const url = `${api?.dashboad?.enquiry_list}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await Axios.get(url);
      
      if (response.data?.data) {
        return {
          data: response.data.data || [],
          totalRecords: response.data.totalRecords || response.data.count || 0,
        };
      }

      return {
        data: [],
        totalRecords: 0,
      };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || "Network Error");
    }
  }
);
